/**
 * Admin · Users / allowlist (§21, §19.1 / §B-6). MANAGER sees the read-only
 * table; ADMIN creates / edits / deletes users and manages packer PINs. pinHash
 * is never fetched — only a hasPin flag. Writes go through the ADMIN API
 * (audited, optimistic concurrency, last-admin/self guards).
 */
import { useCallback, useMemo, useState } from "react";
import { ApiError, api, type AdminUser } from "../../lib/api.js";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { formatAge } from "../../lib/age.js";
import { Badge, StateChip, DataTable, type Column, SearchBar, Button, EmptyState, ErrorState, SkeletonRows, useToast } from "../../components/ui/index.js";
import { UserEditor } from "./users/UserEditor.js";

export function Users() {
  const { user: me } = useSession();
  const isAdmin = me?.role === "ADMIN";
  const r = useResource(api.admin.users);
  const { toast } = useToast();
  const [editing, setEditing] = useState<AdminUser | null | "new">(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState(""); // Task 1 — client-side find (small list)

  const reload = r.reload;
  const users = useMemo(() => r.data?.users ?? [], [r.data]);
  // Client-side filter across name · email · role (the list is fully loaded).
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.email ?? "", u.role].some((f) => f.toLowerCase().includes(q)),
    );
  }, [users, search]);

  const remove = useCallback(
    async (u: AdminUser) => {
      if (!window.confirm(`Remove ${u.name} from the allowlist?`)) return;
      setBusy(true);
      try {
        await api.admin.deleteUser(u.id);
        toast("User removed", "success");
        reload();
      } catch (e) {
        const code = e instanceof ApiError ? (e.detail as { code?: string } | undefined)?.code : undefined;
        toast(
          code === "LAST_ADMIN" ? "Can’t delete the last active admin." : code === "SELF_DELETE" ? "You can’t delete your own account." : "Couldn’t remove the user.",
          "danger",
        );
      } finally {
        setBusy(false);
      }
    },
    [reload, toast],
  );

  const columns = useMemo<Array<Column<AdminUser>>>(() => {
    const base: Array<Column<AdminUser>> = [
      {
        key: "user",
        header: "User",
        cell: (u) => (
          <span className="cell-stack">
            <span className="cell-strong">{u.name}</span>
            <span className="cell-sub">{u.email ?? "—"}</span>
          </span>
        ),
      },
      { key: "role", header: "Role", cell: (u) => <Badge>{u.role}</Badge> },
      { key: "pin", header: "PIN", cell: (u) => (u.hasPin ? <Badge>set</Badge> : <span className="muted">—</span>) },
      { key: "created", header: "Added", cell: (u) => <span title={u.createdAt}>{formatAge(u.createdAt)}</span> },
      { key: "active", header: "Status", cell: (u) => <StateChip on={u.active} onLabel="Active" offLabel="Inactive" /> },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      {
        key: "actions",
        header: "",
        cell: (u) => (
          <span className="row-actions">
            <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${u.name}`} onClick={() => setEditing(u)} />
            <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Remove ${u.name}`} disabled={busy} onClick={() => void remove(u)} />
          </span>
        ),
      },
    ];
  }, [isAdmin, busy, remove]);

  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Users</h1>
          {r.data && (
            <span className="page-head__count">
              {search.trim() ? `${shown.length} of ${users.length}` : users.length} users
            </span>
          )}
        </div>
        {isAdmin ? (
          <div className="page-head__actions">
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>Add user</Button>
          </div>
        ) : (
          <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>
        )}
      </div>

      {r.data && users.length > 0 && (
        <div className="toolbar">
          <SearchBar
            className="toolbar__search"
            ariaLabel="Search users"
            placeholder="Search name, email, role…"
            focusShortcut="/"
            value={search}
            onChange={setSearch}
          />
        </div>
      )}

      {r.error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
      ) : r.loading && !r.data ? (
        <div className="table-card"><SkeletonRows rows={5} cols={isAdmin ? 6 : 5} /></div>
      ) : users.length === 0 ? (
        <EmptyState icon="users" title="No users yet" hint={isAdmin ? "Add managers/admins (by email) and packers (with a PIN)." : "Users appear after their first sign-in."} />
      ) : shown.length === 0 ? (
        <EmptyState icon="search" title="No matching users" hint="No users match your search. Clear it to see all." />
      ) : (
        <div aria-busy={busy}>
          <DataTable ariaLabel="Users" rows={shown} getRowKey={(u) => u.id} columns={columns} />
        </div>
      )}

      {editing !== null && (
        <UserEditor
          user={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}
